import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, PencilLine, CaretDown, X, Folder } from "@phosphor-icons/react";
import api, { apiError } from "../lib/api";
import { zar } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";

const UNITS = ["kg", "g", "L", "ml", "unit", "portion"];
const PREP_NOTES = ["Juice", "Fine Slice", "Fine Brunoise", "Medium Brunoise", "Chiffonade", "Chopped",
  "Toasted", "Picked", "Small Cubes", "Peeled", "Chilled", "Mashed", "Whisked", "Noisette", "Puree", "Melted"];
const CONV = { kg: ["kg", 1], g: ["kg", 0.001], l: ["l", 1], ml: ["l", 0.001], unit: ["unit", 1], portion: ["unit", 1], ea: ["unit", 1] };
// convert qty in `unit` into `base` (same family): 50 ml into a ml-base sub-recipe = 50, 0.5 kg into a g-base = 500
const toBase = (qty, unit, base) => {
  const u = CONV[String(unit || "").toLowerCase()];
  const b = CONV[String(base || "").toLowerCase()];
  if (!u || !b || u[0] !== b[0]) return null;
  return (qty * u[1]) / b[1];
};

const EMPTY_ING = { ingredient_type: "stock_item", stock_item_id: "", sub_recipe_id: "", quantity: "", unit_of_measure: "unit", preparation_note: "" };
const EMPTY_RECIPE = { name: "", category_id: "", recipe_type: "dish", yield_quantity: "", yield_unit: "portion",
  wastage_percent: "", selling_price: "", storage_instructions: "", shelf_life: "", method_steps: "", status: "active", ingredients: [] };

const fromRecipe = (r) => ({
  name: r.name, category_id: r.category_id || "", recipe_type: r.recipe_type,
  yield_quantity: r.yield_quantity, yield_unit: r.yield_unit, wastage_percent: r.wastage_percent ?? "",
  selling_price: r.selling_price ?? "", storage_instructions: r.storage_instructions || "",
  shelf_life: r.shelf_life || "", method_steps: (r.method_steps || []).join("\n"), status: r.status || "active",
  ingredients: (r.ingredients || []).map((i) => ({ ...EMPTY_ING, ...i })),
});

function RecipeBook() {
  const [recipes, setRecipes] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterCat, setFilterCat] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_RECIPE);
  const [busy, setBusy] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [newCat, setNewCat] = useState("");

  const load = () => {
    api.get("/recipes").then((r) => setRecipes(r.data)).catch(() => toast.error("Failed to load recipes"));
    api.get("/stock-items").then((r) => setStockItems(r.data)).catch(() => {});
    api.get("/recipe-categories").then((r) => setCategories(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";
  const subRecipes = recipes.filter((r) => r.recipe_type === "component" && r.status === "active" && r.id !== editing?.id);

  // Live cost preview (mirrors the server formula; server is authoritative on save)
  const cost = useMemo(() => {
    const lines = form.ingredients.map((ing) => {
      let lineCost = null, refName = "—", expectedUnit = null, est = false, noCost = false;
      if (ing.ingredient_type === "stock_item") {
        const it = stockItems.find((s) => s.id === ing.stock_item_id);
        refName = it?.name || "—";
        expectedUnit = it?.base_unit;
        const qb = it ? toBase(Number(ing.quantity) || 0, ing.unit_of_measure, it.base_unit) : null;
        if (qb != null) {
          if ((it.current_average_cost || 0) > 0) lineCost = qb * it.current_average_cost;
          else if (it.catalog_estimate_per_base) { lineCost = qb * it.catalog_estimate_per_base; est = true; }
          else noCost = true;
        }
      } else {
        const sub = recipes.find((r) => r.id === ing.sub_recipe_id);
        refName = sub?.name || "—";
        expectedUnit = sub?.yield_unit;
        const yb = sub ? (sub.yield_unit === "portion" ? "unit" : sub.yield_unit) : null;
        const qb = sub ? toBase(Number(ing.quantity) || 0, ing.unit_of_measure, yb) : null;
        if (qb != null && sub?.cost_per_yield_unit != null) lineCost = qb * sub.cost_per_yield_unit;
      }
      const mismatch = expectedUnit && lineCost == null && !noCost && Number(ing.quantity) > 0;
      return { ...ing, cost: lineCost, ref_name: refName, mismatch, expectedUnit, est, noCost };
    });
    const subtotal = lines.reduce((a, l) => a + (l.cost || 0), 0);
    const total = subtotal * (1 + (Number(form.wastage_percent) || 0) / 100);
    const cpu = Number(form.yield_quantity) > 0 ? total / Number(form.yield_quantity) : null;
    return { lines, subtotal, total, cpu };
  }, [form.ingredients, form.wastage_percent, form.yield_quantity, stockItems, recipes]);

  const setIng = (i, patch) => setForm({ ...form, ingredients: form.ingredients.map((x, j) => (j === i ? { ...x, ...patch } : x)) });

  const openAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY_RECIPE, wastage_percent: localStorage.getItem("lastWastage") || "" });
    setOpen(true);
  };
  const openEdit = (r) => { setEditing(r); setForm(fromRecipe(r)); setOpen(true); };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Recipe name required");
    if (!(Number(form.yield_quantity) > 0)) return toast.error("Yield quantity is required");
    setBusy(true);
    const payload = {
      ...form,
      category_id: form.category_id || null,
      yield_quantity: Number(form.yield_quantity),
      wastage_percent: Number(form.wastage_percent) || 0,
      selling_price: form.recipe_type === "dish" && form.selling_price !== "" ? Number(form.selling_price) : null,
      method_steps: form.method_steps.split("\n").map((s) => s.trim()).filter(Boolean),
      ingredients: form.ingredients
        .filter((i) => (i.ingredient_type === "stock_item" ? i.stock_item_id : i.sub_recipe_id) && Number(i.quantity) > 0)
        .map((i) => ({ ...i, quantity: Number(i.quantity), stock_item_id: i.ingredient_type === "stock_item" ? i.stock_item_id : null,
                        sub_recipe_id: i.ingredient_type === "sub_recipe" ? i.sub_recipe_id : null, preparation_note: i.preparation_note || null })),
    };
    try {
      if (editing) { await api.put(`/recipes/${editing.id}`, payload); toast.success("Recipe updated"); }
      else { await api.post("/recipes", payload); toast.success("Recipe added"); }
      localStorage.setItem("lastWastage", String(payload.wastage_percent));
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try { await api.post("/recipe-categories", { name: newCat.trim() }); setNewCat(""); load(); toast.success("Category added"); }
    catch (e) { toast.error(apiError(e, "Add failed")); }
  };
  const toggleCatArchived = async (c) => {
    try { await api.put(`/recipe-categories/${c.id}`, { name: c.name, archived: !c.archived }); load(); }
    catch (e) { toast.error(apiError(e, "Update failed")); }
  };

  const visible = recipes.filter((r) =>
    (showArchived || r.status !== "archived") && (filterCat === "all" || r.category_id === filterCat)
    && (filterType === "all" || r.recipe_type === filterType));

  return (
    <div className="space-y-6" data-testid="recipes-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Recipes</h1>
          <p className="text-muted-foreground mt-1">{recipes.filter((r) => r.recipe_type === "dish" && r.status === "active").length} dishes · {recipes.filter((r) => r.recipe_type === "component" && r.status === "active").length} components — costs update live from purchase prices</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex rounded-md border border-border overflow-hidden glass" data-testid="recipe-type-filter">
            {[["all", "All"], ["dish", "Dishes"], ["component", "Components"]].map(([v, label]) => (
              <button key={v} data-testid={`recipe-filter-${v}`} onClick={() => setFilterType(v)}
                className={`px-3 h-9 text-sm transition-colors ${filterType === v ? "bg-primary text-white" : "text-foreground hover:bg-accent"}`}>
                {label}
              </button>
            ))}
          </div>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-44" data-testid="recipe-filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.filter((c) => !c.archived).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" data-testid="manage-recipe-categories-btn" onClick={() => setCatOpen(true)}><Folder size={15} className="mr-1" /> Categories</Button>
          <Button data-testid="add-recipe-btn" onClick={openAdd} className="bg-primary hover:bg-[#8B5CF6] text-white"><Plus size={16} className="mr-1" /> Add recipe</Button>
        </div>
      </div>

      <div className="glass border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]" data-testid="recipes-table">
          <thead><tr className="border-b text-left text-muted-foreground">
            <th className="px-4 py-3 font-medium">Name</th><th className="px-4 py-3 font-medium">Category</th><th className="px-4 py-3 font-medium">Type</th>
            <th className="px-4 py-3 font-medium">Yield</th><th className="px-4 py-3 font-medium text-right">Cost / yield unit</th>
            <th className="px-4 py-3 font-medium text-right">Selling price</th><th className="px-4 py-3 font-medium text-right">Margin %</th>
            <th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3 font-medium"></th>
          </tr></thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.id} className={`border-b last:border-0 ${r.status === "archived" ? "opacity-50" : ""}`} data-testid={`recipe-row-${r.id}`}>
                <td className="px-4 py-2.5 font-medium">{r.name}{r.cost_complete === false && r.missing_cost && <span className="ml-1.5 text-[10px] text-[#F87171]" title="Some ingredient costs can't be computed">cost incomplete</span>}{r.has_estimates && !r.missing_cost && <span className="ml-1.5 text-[10px] text-[#F4A261]" title="Priced from catalog list prices — no invoiced purchases yet" data-testid={`recipe-estimated-${r.id}`}>estimated</span>}</td>
                <td className="px-4 py-2.5">{catName(r.category_id)}</td>
                <td className="px-4 py-2.5"><Badge variant="outline" className={r.recipe_type === "dish" ? "border-[#A78BFA]/40 text-[#A78BFA]" : "border-[#2B6CB0]/40 text-[#2B6CB0]"}>{r.recipe_type === "dish" ? "Dish" : "Component"}</Badge></td>
                <td className="px-4 py-2.5">{r.yield_quantity} {r.yield_unit}</td>
                <td className="px-4 py-2.5 num text-right">{r.cost_per_yield_unit != null ? zar(r.cost_per_yield_unit) : "—"}</td>
                <td className="px-4 py-2.5 num text-right">{r.selling_price != null ? zar(r.selling_price) : "—"}</td>
                <td className={`px-4 py-2.5 num text-right ${r.margin_pct != null && r.margin_pct < 50 ? "text-[#F87171]" : r.margin_estimated ? "text-[#F4A261]" : ""}`} title={r.margin_estimated ? "Margin computed from catalog list-price estimates" : undefined} data-testid={`recipe-margin-cell-${r.id}`}>{r.margin_pct != null ? `${r.margin_pct}%${r.margin_estimated ? " est" : ""}` : "—"}</td>
                <td className="px-4 py-2.5"><Badge variant="outline" className={r.status === "archived" ? "text-muted-foreground" : "border-[#4A5D23]/40 text-[#A9C97E]"}>{r.status === "archived" ? "Archived" : "Active"}</Badge></td>
                <td className="px-4 py-2.5 text-right"><Button size="sm" variant="ghost" data-testid={`recipe-edit-${r.id}`} onClick={() => openEdit(r)}><PencilLine size={15} /></Button></td>
              </tr>
            ))}
            {visible.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">No recipes yet — add your first recipe or sub-recipe.</td></tr>}
          </tbody>
        </table>
      </div>
      <label className="flex items-center gap-2 text-sm text-muted-foreground"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="recipes-show-archived" /> Show archived</label>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="recipe-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit recipe" : "Add recipe"}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Recipe editor with live costing</DialogDescription>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2 sm:col-span-1"><Label>Name</Label><Input data-testid="recipe-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={form.category_id || "none"} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="recipe-category"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{categories.filter((c) => !c.archived).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={form.recipe_type} onValueChange={(v) => setForm({ ...form, recipe_type: v })}>
                  <SelectTrigger data-testid="recipe-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="dish" data-testid="recipe-type-dish">Dish (sellable)</SelectItem><SelectItem value="component" data-testid="recipe-type-component">Component (sub-recipe)</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5"><Label>Yield qty <span className="text-[#F87171]">*</span></Label><Input type="number" step="any" min="0" data-testid="recipe-yield-qty" value={form.yield_quantity} onChange={(e) => setForm({ ...form, yield_quantity: e.target.value })} /></div>
                <div className="space-y-1.5">
                  <Label>Yield unit <span className="text-[#F87171]">*</span></Label>
                  <Select value={form.yield_unit} onValueChange={(v) => setForm({ ...form, yield_unit: v })}>
                    <SelectTrigger data-testid="recipe-yield-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5"><Label>Wastage %</Label><Input type="number" step="any" min="0" data-testid="recipe-wastage" value={form.wastage_percent} onChange={(e) => setForm({ ...form, wastage_percent: e.target.value })} /></div>
              {form.recipe_type === "dish" && (
                <div className="space-y-1.5"><Label>Sell price (R, VAT-inclusive — required for dishes)</Label><Input type="number" step="any" min="0" data-testid="recipe-price" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} /></div>
              )}
              <div className="space-y-1.5"><Label>Storage</Label><Input data-testid="recipe-storage" value={form.storage_instructions} onChange={(e) => setForm({ ...form, storage_instructions: e.target.value })} placeholder="e.g. Refrigerate below 4°C" /></div>
              <div className="space-y-1.5"><Label>Shelf life</Label><Input data-testid="recipe-shelf-life" value={form.shelf_life} onChange={(e) => setForm({ ...form, shelf_life: e.target.value })} placeholder="e.g. 3 days" /></div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="font-heading font-semibold text-sm">Ingredients</p>
                <Button size="sm" variant="outline" data-testid="recipe-add-ingredient" onClick={() => setForm({ ...form, ingredients: [...form.ingredients, { ...EMPTY_ING }] })}><Plus size={14} className="mr-1" /> Add</Button>
              </div>
              <div className="space-y-2" data-testid="recipe-ingredients">
                {cost.lines.map((l, i) => (
                  <div key={i} className="border border-border rounded-lg p-2.5 space-y-2" data-testid={`recipe-ingredient-${i}`}>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                      <Select value={l.ingredient_type} onValueChange={(v) => setIng(i, { ingredient_type: v, stock_item_id: "", sub_recipe_id: "" })}>
                        <SelectTrigger data-testid={`ing-type-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="stock_item">Stock item</SelectItem><SelectItem value="sub_recipe">Sub-recipe</SelectItem></SelectContent>
                      </Select>
                      <div className="col-span-2">
                        {l.ingredient_type === "stock_item" ? (
                          <Select value={l.stock_item_id} onValueChange={(v) => { const it = stockItems.find((s) => s.id === v); setIng(i, { stock_item_id: v, unit_of_measure: it?.base_unit || l.unit_of_measure }); }}>
                            <SelectTrigger data-testid={`ing-item-${i}`}><SelectValue placeholder="Pick stock item…" /></SelectTrigger>
                            <SelectContent>{stockItems.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.base_unit}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : (
                          <Select value={l.sub_recipe_id} onValueChange={(v) => { const sub = recipes.find((r) => r.id === v); setIng(i, { sub_recipe_id: v, unit_of_measure: sub?.yield_unit || l.unit_of_measure }); }}>
                            <SelectTrigger data-testid={`ing-sub-${i}`}><SelectValue placeholder="Pick sub-recipe…" /></SelectTrigger>
                            <SelectContent>{subRecipes.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} · {r.yield_unit}</SelectItem>)}</SelectContent>
                          </Select>
                        )}
                      </div>
                      <Input type="number" step="any" min="0" placeholder="Qty" value={l.quantity} onChange={(e) => setIng(i, { quantity: e.target.value })} data-testid={`ing-qty-${i}`} />
                      <div className="flex gap-1">
                        <Select value={l.unit_of_measure} onValueChange={(v) => setIng(i, { unit_of_measure: v })}>
                          <SelectTrigger data-testid={`ing-unit-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button size="sm" variant="ghost" className="text-destructive px-2" data-testid={`ing-remove-${i}`} onClick={() => setForm({ ...form, ingredients: form.ingredients.filter((_, j) => j !== i) })}><X size={14} /></Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input list="prep-notes" placeholder="Prep note (optional)" value={l.preparation_note || ""} onChange={(e) => setIng(i, { preparation_note: e.target.value })} data-testid={`ing-prep-${i}`} className="text-xs" />
                      <span className="text-sm num whitespace-nowrap w-24 text-right" data-testid={`ing-cost-${i}`}>
                        {l.cost != null ? zar(l.cost) : "—"}
                        {l.est && <span className="ml-1 text-[10px] text-[#F4A261]" title="Estimated from catalog list price — no invoiced purchase yet" data-testid={`ing-est-${i}`}>est</span>}
                      </span>
                    </div>
                    {l.noCost && (
                      <p className="text-xs text-[#F87171]" data-testid={`ing-nocost-${i}`}>
                        No cost data — no invoiced purchase or catalog list price for this item yet.
                      </p>
                    )}
                    {l.mismatch && (
                      <p className="text-xs text-[#F87171]" data-testid={`ing-mismatch-${i}`}>
                        Unit mismatch — {l.ingredient_type === "sub_recipe" ? "this component yields in" : "this stock item is measured in"} <strong>{l.expectedUnit}</strong>; change the qty unit to {l.expectedUnit} (or a compatible unit).
                      </p>
                    )}
                  </div>
                ))}
                <datalist id="prep-notes">{PREP_NOTES.map((p) => <option key={p} value={p} />)}</datalist>
                {form.ingredients.length === 0 && <p className="text-xs text-muted-foreground py-2">No ingredients yet.</p>}
              </div>
            </div>

            <div className="bg-accent/50 rounded-lg p-4 space-y-1" data-testid="recipe-cost-panel">
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Ingredients subtotal</span><span className="num" data-testid="recipe-subtotal">{zar(cost.subtotal)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">With {Number(form.wastage_percent) || 0}% wastage</span><span className="num" data-testid="recipe-total">{zar(cost.total)}</span></div>
              <div className="flex justify-between font-heading font-semibold"><span>Cost per {form.yield_unit}</span><span className="num" data-testid="recipe-cpu">{cost.cpu != null ? zar(cost.cpu) : "—"}</span></div>
              {cost.lines.some((l) => l.est) && (
                <p className="text-[11px] text-[#F4A261] pt-1" data-testid="recipe-has-estimates">Includes catalog list-price estimates — margins shown are estimates until real invoices post.</p>
              )}
              {form.recipe_type === "dish" && form.selling_price !== "" && cost.cpu != null && form.yield_unit === "portion" && cost.lines.every((l) => l.cost != null) && (
                <div className="flex justify-between text-sm pt-1 border-t border-border"><span className="text-muted-foreground">Margin</span><span className="num" data-testid="recipe-margin">{zar(Number(form.selling_price) - cost.cpu)} ({(((Number(form.selling_price) - cost.cpu) / Number(form.selling_price)) * 100).toFixed(1)}%){cost.lines.some((l) => l.est) && <span className="ml-1 text-[10px] text-[#F4A261]" title="Margin uses catalog list-price estimates" data-testid="recipe-margin-est">est</span>}</span></div>
              )}
            </div>

            <div className="space-y-1.5"><Label>Method (one step per line)</Label><Textarea rows={3} data-testid="recipe-method" value={form.method_steps} onChange={(e) => setForm({ ...form, method_steps: e.target.value })} /></div>
            {editing && (
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="recipe-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="archived">Archived</SelectItem></SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={submit} disabled={busy} data-testid="recipe-save-btn" className="w-full bg-primary hover:bg-[#8B5CF6] text-white">
              {busy ? "Saving…" : editing ? "Save changes" : "Add recipe"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent className="max-w-sm" data-testid="recipe-categories-dialog">
          <DialogHeader><DialogTitle className="font-heading">Recipe categories</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Manage recipe categories</DialogDescription>
          <div className="space-y-2">
            {categories.map((c) => (
              <div key={c.id} className="flex items-center justify-between border border-border rounded-md px-3 py-2 text-sm" data-testid={`recipe-cat-${c.id}`}>
                <span className={c.archived ? "line-through text-muted-foreground" : ""}>{c.name}</span>
                <Button size="sm" variant="ghost" data-testid={`recipe-cat-toggle-${c.id}`} onClick={() => toggleCatArchived(c)}>{c.archived ? "Restore" : "Archive"}</Button>
              </div>
            ))}
            <div className="flex gap-2 pt-2">
              <Input placeholder="New category…" value={newCat} onChange={(e) => setNewCat(e.target.value)} data-testid="recipe-cat-new" />
              <Button onClick={addCategory} data-testid="recipe-cat-add" className="bg-primary hover:bg-[#8B5CF6] text-white">Add</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function IngredientDirectory() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/stock/ingredients").then((r) => setItems(r.data)).catch(() => toast.error("Failed to load ingredient directory"));
  }, []);
  return (
    <div className="glass border border-border rounded-lg overflow-x-auto" data-testid="ingredient-directory">
      <table className="w-full text-sm min-w-[760px]">
        <thead><tr className="border-b text-left text-muted-foreground">
          <th className="px-4 py-3 font-medium">Ingredient (stock item)</th>
          <th className="px-4 py-3 font-medium">Base unit</th>
          <th className="px-4 py-3 font-medium text-right">Avg cost / unit</th>
          <th className="px-4 py-3 font-medium text-right">On hand</th>
          <th className="px-4 py-3 font-medium">Used in recipes</th>
        </tr></thead>
        <tbody>
          {(items || []).map((i) => (
            <tr key={i.id} className="border-b last:border-0" data-testid={`ingredient-row-${i.id}`}>
              <td className="px-4 py-2.5 font-medium">{i.name}</td>
              <td className="px-4 py-2.5">{i.base_unit}</td>
              <td className="px-4 py-2.5 num text-right">{i.current_average_cost != null ? zar(i.current_average_cost) : "—"}</td>
              <td className="px-4 py-2.5 num text-right">{i.qty_on_hand ?? 0}</td>
              <td className="px-4 py-2.5">
                <div className="flex flex-wrap gap-1">
                  {i.used_in.map((u) => <Badge key={u.recipe_id} variant="outline" className="text-[10px]" data-testid={`ingredient-used-${i.id}-${u.recipe_id}`}>{u.name}</Badge>)}
                  {i.used_in.length === 0 && <span className="text-xs text-muted-foreground">Not used in any recipe</span>}
                </div>
              </td>
            </tr>
          ))}
          {items && items.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No stock items yet — capture supplier invoices to build the catalog.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function Recipes() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "book";
  return (
    <div className="space-y-6">
      <Tabs value={tab} onValueChange={(v) => setParams(v === "book" ? {} : { tab: v })}>
        <TabsList data-testid="recipes-tabs">
          <TabsTrigger value="book" data-testid="tab-recipe-book">Recipe Book</TabsTrigger>
          <TabsTrigger value="ingredients" data-testid="tab-ingredients">Ingredient Directory</TabsTrigger>
        </TabsList>
        <TabsContent value="book" className="mt-4"><RecipeBook /></TabsContent>
        <TabsContent value="ingredients" className="mt-4 space-y-3">
          <p className="text-sm text-muted-foreground">Every stock item with its live average cost and the recipes that reference it. This is the StockItem catalog — managed under Inventory, framed here for recipe building.</p>
          <IngredientDirectory />
        </TabsContent>
      </Tabs>
    </div>
  );
}

